<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { authMixin, deviceMixin } from "@/mixins";
import { API_ENDPOINTS, PAGE_OPTIONS } from "@/constants";
import { requireAuth } from "@/utils/auth";
import { apiGet } from "@/utils/request";

/**
 * 操作日志页面
 */
export default {
  page() {
    return {
      title: this.$t("menuitems.setting.actionlog"),
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  mixins: [authMixin, deviceMixin],
  data() {
    return {
      title: "",
      items: [],
      loading: true,
      logList: [],
      // 分页
      currentPage: 1,
      perPage: 20,
      total: 0,
      pageOptions: [10, 20, 50, 100],
      // 筛选
      filterAction: '',
      filterDomain: '',
      actionOptions: [
        { value: '', label: '全部操作' },
        { value: 'add_domain', label: '添加域名' },
        { value: 'delete_domain', label: '删除域名' },
        { value: 'suspend', label: '暂停域名' },
        { value: 'recovery', label: '恢复域名' },
        { value: 'add_record', label: '添加记录' },
        { value: 'edit_record', label: '编辑记录' },
        { value: 'delete_record', label: '删除记录' },
        { value: 'disable', label: '禁用记录' },
        { value: 'recovery', label: '启用记录' },
        { value: 'dnssec_enable', label: '启用DNSSEC' },
        { value: 'dnssec_disable', label: '禁用DNSSEC' },
      ],
    };
  },
  computed: {
    // 过滤后的日志列表
    filteredLogList() {
      let list = this.logList;
      
      // 按操作类型筛选
      if (this.filterAction) {
        list = list.filter(log => log.action === this.filterAction);
      }
      
      // 按域名筛选
      if (this.filterDomain) {
        const domain = this.filterDomain.toLowerCase();
        list = list.filter(log => 
          log.domain && log.domain.toLowerCase().includes(domain)
        );
      }
      
      return list;
    },
    
    // 分页后的日志列表
    paginatedLogList() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredLogList.slice(start, end);
    },
    
    // 总页数
    totalPages() {
      return Math.ceil(this.filteredLogList.length / this.perPage);
    },
  },
  mounted() {
    this.initPage();
    this.getLogList();
  },
  methods: {
    /**
     * 初始化页面
     */
    initPage() {
      this.title = this.$t("menuitems.setting.actionlog");
      this.items = [
        {
          text: this.$t("dashboard.text"),
          href: "/",
        },
        {
          text: this.$t("menuitems.setting.actionlog"),
          active: true,
        },
      ];
    },
    
    /**
     * 获取操作日志列表
     */
    async getLogList() {
      if (!requireAuth(this)) {
        return;
      }
      
      try {
        const response = await apiGet(
          '/getactionlog',
          {
            page: this.currentPage,
            per_page: this.perPage,
          },
          {
            vm: this,
            showLoading: true,
            loadingText: '加载操作日志...'
          }
        );
        
        if (response && response.status === 200) {
          this.loading = false;
          this.logList = response.data || [];
          this.total = response.pagination ? response.pagination.total : this.logList.length;
          this.currentPage = response.pagination ? response.pagination.page : 1;
        }
      } catch (error) {
        console.error('加载操作日志失败:', error);
        // handleApiResponse 已经处理了错误消息
        if (error.response && error.response.status !== 401) {
          // 其他错误已在handleApiResponse中处理
        }
      }
    },
    
    /**
     * 处理页码变化
     */
    handleCurrentChange(page) {
      this.currentPage = page;
      // 如果使用后端分页，重新加载数据
      // this.getLogList();
    },
    
    /**
     * 处理每页条数变化
     */
    handleSizeChange(size) {
      this.perPage = size;
      this.currentPage = 1;
      // 如果使用后端分页，重新加载数据
      // this.getLogList();
    },
    
    /**
     * 获取操作类型标签
     */
    getActionType(action) {
      const actionMap = {
        'add_domain': { type: 'success', label: '添加域名' },
        'delete_domain': { type: 'danger', label: '删除域名' },
        'suspend': { type: 'warning', label: '暂停域名' },
        'recovery': { type: 'info', label: '恢复域名' },
        'add_record': { type: 'success', label: '添加记录' },
        'edit_record': { type: 'primary', label: '编辑记录' },
        'delete_record': { type: 'danger', label: '删除记录' },
        'disable': { type: 'warning', label: '禁用记录' },
        'dnssec_enable': { type: 'success', label: '启用DNSSEC' },
        'dnssec_disable': { type: 'info', label: '禁用DNSSEC' },
      };
      return actionMap[action] || { type: '', label: action };
    },
    
    /**
     * 格式化时间
     */
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    /**
     * 清除筛选
     */
    clearFilters() {
      this.filterAction = '';
      this.filterDomain = '';
      this.currentPage = 1;
    },
    
    /**
     * 刷新日志列表
     */
    refreshLogList() {
      this.getLogList();
    },
    
    /**
     * 按时间排序
     */
    sortByTime(a, b) {
      const timeA = a.time || 0;
      const timeB = b.time || 0;
      return timeB - timeA; // 降序排列
    },
    
    /**
     * 跳转到域名记录页面
     */
    torecord(domain) {
      this.$router.push({
        path: `/dns/${domain}/record`,
      });
    },
  },
  filters: {
    formatTimer(value) {
      if (!value) return '-';
      const date = new Date(value * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body" style="min-height: auto;">
            <!-- 筛选栏 -->
            <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
              <el-select
                v-model="filterAction"
                placeholder="筛选操作类型"
                clearable
                style="width: 160px;"
                @change="currentPage = 1"
              >
                <el-option
                  v-for="option in actionOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                >
                </el-option>
              </el-select>
              
              <el-input
                v-model="filterDomain"
                placeholder="搜索域名"
                clearable
                style="width: 200px;"
                prefix-icon="el-icon-search"
                @input="currentPage = 1"
              >
              </el-input>
              
              <el-button 
                type="default" 
                icon="el-icon-refresh" 
                @click="refreshLogList"
                :loading="loading"
              >
                刷新
              </el-button>
              
              <el-button 
                v-if="filterAction || filterDomain"
                type="info" 
                icon="el-icon-close" 
                @click="clearFilters"
              >
                清除筛选
              </el-button>
              
              <div style="margin-left: auto; color: #909399; font-size: 14px;">
                共 {{ filteredLogList.length }} 条记录
              </div>
            </div>
            
            <!-- Element UI 表格 -->
            <el-table
              :data="paginatedLogList"
              stripe
              border
              style="width: 100%"
              :fit="true"
              :empty-text="$t('dns.list.no_data') || '暂无数据'"
              tooltip-effect="dark"
              highlight-current-row
              v-loading="loading"
              element-loading-text="加载中..."
            >
              <!-- ID列 -->
              <el-table-column
                label="ID"
                prop="id"
                width="80"
                align="center"
                sortable
              >
              </el-table-column>
              
              <!-- 操作类型列 -->
              <el-table-column
                :label="$t('dns.list.action') || '操作类型'"
                prop="action"
                width="140"
                align="center"
              >
                <template slot-scope="scope">
                  <el-tag
                    :type="getActionType(scope.row.action).type"
                    size="small"
                    effect="plain"
                  >
                    {{ getActionType(scope.row.action).label }}
                  </el-tag>
                </template>
              </el-table-column>
              
              <!-- 域名列 -->
              <el-table-column
                :label="$t('dns.list.domain') || '域名'"
                prop="domain"
                min-width="180"
                show-overflow-tooltip
              >
                <template slot-scope="scope">
                  <el-link 
                    v-if="scope.row.domain" 
                    type="primary" 
                    @click="torecord(scope.row.domain)"
                    :underline="false"
                  >
                    {{ scope.row.domain }}
                  </el-link>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              
              <!-- 记录列 -->
              <el-table-column
                label="记录"
                prop="record"
                min-width="150"
                show-overflow-tooltip
              >
                <template slot-scope="scope">
                  <span v-if="scope.row.record">{{ scope.row.record }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              
              <!-- 内容列 -->
              <el-table-column
                label="内容"
                prop="content"
                min-width="200"
                show-overflow-tooltip
              >
                <template slot-scope="scope">
                  <span v-if="scope.row.content" :title="scope.row.content">
                    {{ scope.row.content.length > 50 ? scope.row.content.substring(0, 50) + '...' : scope.row.content }}
                  </span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              
              <!-- 操作时间列 -->
              <el-table-column
                :label="$t('dns.list.lastday') || '操作时间'"
                prop="time"
                width="180"
                align="center"
                sortable
                :sort-method="sortByTime"
              >
                <template slot-scope="scope">
                  <span :title="formatTime(scope.row.time)">
                    {{ formatTime(scope.row.time) }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
            
            <!-- 分页 -->
            <div style="margin-top: 20px; text-align: center;">
              <el-pagination
                background
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page.sync="currentPage"
                :page-sizes="pageOptions"
                :page-size.sync="perPage"
                layout="total, sizes, prev, pager, next, jumper"
                :total="filteredLogList.length"
              >
              </el-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>